import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { toastStore } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { ChatbotConversation } from 'entities/Chatbot'
import { ChatbotConversationApi } from 'entities/Chatbot/api/conversation'
import {
  ChatbotConversationStatus,
  type IChatbotConversationStatus,
} from 'entities/Chatbot/model/type'
import type { UnifiedAgentStore } from 'pages/chatbot/ui/UnifiedAgent/store/unifiedAgentStore'

export class TestAgentStore {
  private _uuid: string | null = null
  private _started = false
  private _loading = false
  private _status: IChatbotConversationStatus | null = null
  private _conversation = new ChatbotConversation()
  private _inputValue = ''
  private _shouldShowIncomingMessage = false
  private _pendingUserMessage: string | null = null
  private _isModalOpen = false

  constructor(private readonly agent: UnifiedAgentStore) {
    makeAutoObservable(this)
  }

  get started() {
    return this._started
  }

  get loading() {
    return this._loading
  }

  get status() {
    return this._status
  }

  get inputValue() {
    return this._inputValue
  }

  get shouldShowIncomingMessage() {
    return this._shouldShowIncomingMessage
  }

  get pendingUserMessage() {
    return this._pendingUserMessage
  }

  get isModalOpen() {
    return this._isModalOpen
  }

  get messages() {
    return this._conversation.messages
  }

  get hasMessages() {
    return this._conversation.messages.length > 0
  }

  get completed() {
    return (
      this._status === ChatbotConversationStatus.Completed ||
      this._status === ChatbotConversationStatus.Abandoned ||
      this._status === ChatbotConversationStatus.Terminated
    )
  }

  get isExistChanges() {
    return this.agent.blockerStore?.isExistChanges ?? false
  }

  openModal = () => {
    this._isModalOpen = true
  }

  closeModal = () => {
    this.reset()
    this._isModalOpen = false
  }

  setInputValue = (value: string) => {
    this._inputValue = value
  }

  handleStart = async () => {
    if (this.isExistChanges || this.agent.id === null) {
      const modalId = nanoid()
      const isActive = this.agent.model.isActive
      modalStore.addModal({
        id: modalId,
        type: ModalTypeList.INFO,
        title: isActive ? 'Publish agent before testing' : 'Save agent before testing',
        primaryAction: {
          text: isActive ? 'Publish & Test' : 'Save & Test',
          onAction: async () => {
            modalStore.closeModal(modalId)
            const agentId = await this.agent.saveForTest()
            if (!agentId) return
            this._beginTest()
            await this._startConversation(agentId)
          },
        },
        secondaryAction: {
          text: 'Cancel',
          onAction: () => modalStore.closeModal(modalId),
        },
      })
    } else {
      await this.start()
    }
  }

  start = async (chatbotId?: number | null) => {
    const id = chatbotId ?? this.agent.id
    if (!id) return
    this._beginTest()
    await this._startConversation(id)
  }

  reset = () => {
    this._loading = false
    this._started = false
    this._status = null
    this._uuid = null
    this._conversation.clear()
    this._inputValue = ''
    this._shouldShowIncomingMessage = false
    this._pendingUserMessage = null
    this._isModalOpen = false
  }

  restart = async () => {
    const id = this.agent.id
    if (!id) return

    runInAction(() => {
      this._uuid = null
      this._started = false
      this._status = null
      this._conversation.clear()
    })

    await this.start(id)
  }

  saveAndRestart = async () => {
    const agentId = await this.agent.saveForTest()
    if (agentId) await this.restart()
  }

  sendMessage = async () => {
    const message = this._inputValue.trim()
    if (!message || !this._uuid || this._loading) return

    this._inputValue = ''
    this._loading = true
    this._pendingUserMessage = message

    try {
      const { data } = await ChatbotConversationApi.message(this._uuid, message)

      runInAction(() => {
        this._uuid = data.session_uuid
        this._conversation.addOrUpdate(data.chat)
        this._status = data.status
        this._pendingUserMessage = null
        this._loading = false
      })
    } catch (error) {
      runInAction(() => {
        this._pendingUserMessage = null
        this._loading = false
      })
      toastStore.add({ title: 'Something went wrong. Please try again.', type: 'error' })
    }
  }

  testFollowup = async () => {
    if (!this._uuid || this._loading) return

    runInAction(() => {
      this._loading = true
      this._shouldShowIncomingMessage = true
    })

    try {
      const { data } = await ChatbotConversationApi.testFollowup(this._uuid)

      runInAction(() => {
        this._uuid = data.session_uuid
        this._conversation.addOrUpdate(data.chat)
        this._status = data.status
      })
    } catch (error) {
      toastStore.add({ title: 'Something went wrong. Please try again.', type: 'error' })
    } finally {
      runInAction(() => {
        this._loading = false
        this._shouldShowIncomingMessage = false
      })
    }
  }

  private _beginTest = () => {
    runInAction(() => {
      this._loading = true
      this._started = true
      this._status = null
      this._uuid = null
      this._conversation.clear()
    })
  }

  private _startConversation = async (id: number) => {
    try {
      const { data } = await ChatbotConversationApi.start(id)
      const { data: autoData } = await ChatbotConversationApi.autoStart(data.session_uuid)

      runInAction(() => {
        this._uuid = autoData.session_uuid
        this._conversation.addOrUpdate(autoData.chat)
        this._status = autoData.status
        this._loading = false
      })
    } catch (error) {
      runInAction(() => {
        this._loading = false
        this._started = false
      })
      toastStore.add({ title: 'Something went wrong. Please try again.', type: 'error' })
    }
  }
}
